import { on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as RA from 'ramda-adjunct';
import { ItemsActions, AsyncActions } from './items.actions';
import { ManagementBaseActions } from '../base/management-base.actions';
import {Item} from '@emm/core/models/item.model';
import {asyncActionState, AsyncActionState} from '@emm/core/util/async-action-state';
import {SearchFilter} from '@emm/core/models/search-filter';
import {withLoadables} from '@emm/core/util/ngrx.util';
import {ItemAttribute, ItemAttributeSummary} from '@emm/core/models/item-attribute.model';

export const itemsFeatureKey = 'items';

export interface ItemsState extends EntityState<Item>, AsyncActionState {
    selectedTag: string | null;
    searchFilter: SearchFilter;
    initSize: number;
}

export const adapter: EntityAdapter<Item> = createEntityAdapter<Item>({
    selectId: (item: Item) => item.tag,
    sortComparer: null
});

export const initialState: ItemsState = adapter.getInitialState({
    selectedTag: null,
    searchFilter: null,
    initSize: 20,
    ...asyncActionState.initial
});

const { loadActions, successActions, errorActions } = AsyncActions;

export const itemsReducer = withLoadables<ItemsState>({
    loadActions,
    successActions,
    errorActions
})(
    initialState,

    on(ManagementBaseActions.initItems, () => ({
        ...initialState
    })),

    on(ItemsActions.loadBySize, ItemsActions.deleteSuccess, (state: ItemsState) => ({
        ...state,
        selectedTag: null
    })),

    on(ItemsActions.loadBySizeSuccess, (state: ItemsState, { items }) =>
        RA.isEmptyArray(state.ids) ? adapter.setAll(items, state) : { ...state }
    ),

    on(ItemsActions.loadAllSuccess, (state: ItemsState, { items }) => adapter.setAll(items, state)),

    on(ItemsActions.select, (state: ItemsState, { tag }) => ({
        ...state,
        selectedTag: tag
    })),

    on(ItemsActions.searchItems, (state: ItemsState, { filter }) => ({
        ...state,
        searchFilter: filter
    })),

    on(ItemsActions.toggleEnabledSuccess, (state: ItemsState, action) =>
        adapter.updateOne(
            {
                id: action.item.tag,
                changes: {
                    disabled: !action.item.disabled
                }
            },
            state
        )
    ),

    on(ManagementBaseActions.updateItemSuccess, (state: ItemsState, action) =>
        adapter.updateOne(
            {
                id: action.item.tag,
                changes: {
                    name: action.item.name,
                    locked: action.item.locked,
                    tag: action.item.tag,
                    disabled: action.item.disabled,
                    itemTypeName: action.item.itemTypeName,
                    attributes: (action.item.attributes as ItemAttribute[]).map((attribute) => ({
                        attribute: attribute.tag,
                        value: attribute.value?.toString()
                    })) as ItemAttributeSummary[]
                }
            },
            state
        )
    )
);
